import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Stethoscope, Save, Search, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function MMGSection({ ospite, ospiteId }) {
  const { hasRole } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);

  const [form, setForm] = useState({
    nome: '', cognome: '', telefono: '', email: '', indirizzo_ambulatorio: '', codice_regionale: ''
  });

  useEffect(() => {
    if (ospite?.mmg) {
      setForm({
        nome: ospite.mmg.nome || '',
        cognome: ospite.mmg.cognome || '',
        telefono: ospite.mmg.telefono || '',
        email: ospite.mmg.email || '',
        indirizzo_ambulatorio: ospite.mmg.indirizzo_ambulatorio || '',
        codice_regionale: ospite.mmg.codice_regionale || '',
      });
    } else if (ospite?.medico_base) {
      const parts = ospite.medico_base.split(' ');
      setForm(f => ({ ...f, cognome: parts[0] || '', nome: parts.slice(1).join(' ') || '' }));
    }
  }, [ospite]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchMMG = async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    try {
      const res = await api.get('/utility/mmg', { params: { q } });
      setSuggestions(res.data);
      setShowSuggestions(res.data.length > 0);
    } catch { setSuggestions([]); }
  };

  const handleCognomeChange = (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, cognome: val }));
    searchMMG(val);
  };

  const [mmgId, setMmgId] = useState('');

  useEffect(() => {
    if (ospite?.medico_base_id) setMmgId(ospite.medico_base_id);
  }, [ospite]);

  const selectMMG = (mmg) => {
    setForm({
      nome: mmg.nome || '', cognome: mmg.cognome || '',
      telefono: mmg.telefono || '', email: mmg.email || '',
      indirizzo_ambulatorio: mmg.indirizzo_ambulatorio || '',
      codice_regionale: mmg.codice_regionale || '',
    });
    setMmgId(mmg.id || '');
    setShowSuggestions(false);
    toast.info(`Dott. ${mmg.cognome} ${mmg.nome} selezionato dal database`);
  };

  const handleSave = async () => {
    if (!form.cognome) { toast.error('Inserisci almeno il cognome del medico'); return; }
    setSaving(true);
    try {
      let idMedico = mmgId;
      if (form.nome && form.cognome) {
        const res = await api.post('/utility/mmg', form).catch(() => null);
        if (res?.data?.id) idMedico = res.data.id;
      }
      await api.put(`/ospiti/${ospiteId}`, {
        mmg: { ...form, id: idMedico || '' },
        medico_base: `${form.cognome} ${form.nome}`.trim(),
        medico_base_id: idMedico || '',
      });
      toast.success(idMedico ? 'Medico di base aggiornato e collegato alla rubrica'
        : 'Medico di base aggiornato (nessun collegamento alla rubrica: indica nome e cognome)');
      setEditing(false);
    } catch (e) { toast.error('Errore salvataggio'); }
    setSaving(false);
  };

  const hasData = form.cognome || form.nome;

  return (
    <Card data-testid="mmg-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Stethoscope className="w-4 h-4 text-brand" />Medico di Medicina Generale (MMG)
          </CardTitle>
          {hasRole('coordinatore', 'amministrazione', 'medico', 'infermiere') && (
            !editing ? (
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setEditing(true)} data-testid="edit-mmg-btn">Modifica</Button>
            ) : (
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setEditing(false)}>Annulla</Button>
                <Button size="sm" className="text-xs h-7 bg-brand hover:bg-brand-dark text-white" onClick={handleSave} disabled={saving} data-testid="save-mmg-btn">
                  <Save className="w-3 h-3 mr-1" />{saving ? '...' : 'Salva'}
                </Button>
              </div>
            )
          )}
        </div>
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-3" ref={wrapperRef}>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 text-blue-700 text-[10px]">
              <Search className="w-3 h-3" />Inizia a digitare il cognome: i medici gia registrati appariranno automaticamente
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 relative">
              <div className="space-y-1 relative">
                <Label className="text-xs">Cognome *</Label>
                <Input value={form.cognome} onChange={handleCognomeChange} onFocus={() => { if (suggestions.length) setShowSuggestions(true); }} placeholder="Cognome medico" className="text-xs" data-testid="mmg-cognome" />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 w-72 top-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="mmg-suggestions">
                    {suggestions.map((m, i) => (
                      <button key={m.id || i} className="w-full px-3 py-2 text-left hover:bg-blue-50 text-xs border-b last:border-0" onClick={() => selectMMG(m)} data-testid={`mmg-suggestion-${i}`}>
                        <span className="font-semibold">Dott. {m.cognome} {m.nome}</span>
                        {m.telefono && <span className="text-slate-400 ml-2">Tel: {m.telefono}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1"><Label className="text-xs">Nome</Label>
                <Input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Nome" className="text-xs" data-testid="mmg-nome" /></div>
              <div className="space-y-1"><Label className="text-xs">Telefono</Label>
                <Input value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} placeholder="Telefono" className="text-xs" data-testid="mmg-telefono" /></div>
              <div className="space-y-1"><Label className="text-xs">Email</Label>
                <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" className="text-xs" /></div>
              <div className="space-y-1"><Label className="text-xs">Indirizzo Ambulatorio</Label>
                <Input value={form.indirizzo_ambulatorio} onChange={e => setForm(f => ({ ...f, indirizzo_ambulatorio: e.target.value }))} placeholder="Via, Citta" className="text-xs" /></div>
              <div className="space-y-1"><Label className="text-xs">Codice Regionale</Label>
                <Input value={form.codice_regionale} onChange={e => setForm(f => ({ ...f, codice_regionale: e.target.value }))} placeholder="Codice" className="text-xs" /></div>
            </div>
          </div>
        ) : hasData ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div><span className="text-[10px] text-slate-400 block">Medico</span><span className="font-medium text-slate-800">Dott. {form.cognome} {form.nome}</span></div>
            {form.telefono && <div><span className="text-[10px] text-slate-400 block">Telefono</span><span className="text-slate-800 flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{form.telefono}</span></div>}
            {form.email && <div><span className="text-[10px] text-slate-400 block">Email</span><span className="text-slate-800 flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" />{form.email}</span></div>}
            {form.indirizzo_ambulatorio && <div className="col-span-2"><span className="text-[10px] text-slate-400 block">Ambulatorio</span><span className="text-slate-800 flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{form.indirizzo_ambulatorio}</span></div>}
            {form.codice_regionale && <div><span className="text-[10px] text-slate-400 block">Codice Regionale</span><span className="text-slate-800">{form.codice_regionale}</span></div>}
          </div>
        ) : (
          <p className="text-xs text-slate-400 py-2">Nessun medico di base registrato. Clicca "Modifica" per inserirlo.</p>
        )}
      </CardContent>
    </Card>
  );
}
